import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  Trash,
  UploadSimple,
  CaretLeft,
  CaretRight,
  CaretDown,
  FloppyDisk,
  CheckCircle,
  PlayCircle,
  DotsSixVertical,
} from "@phosphor-icons/react";

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

function DragHandle({ listeners, attributes, label }) {
  return (
    <button
      type="button"
      className="p-2 cursor-grab active:cursor-grabbing text-[#A8A8A1] hover:text-[#1C1C19] touch-none"
      aria-label={label}
      {...listeners}
      {...attributes}
    >
      <DotsSixVertical size={18} weight="bold" />
    </button>
  );
}

function SortableLesson({ id, mIdx, lIdx, lesson, uploadingId, uploadProgress, onUpdate, onRemove, onUpload }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "#FAFAFA" : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className="p-5" data-testid={`lesson-${mIdx}-${lIdx}`}>
      <div className="flex items-start gap-2">
        <DragHandle listeners={listeners} attributes={attributes} label={`Reorder lesson ${lIdx + 1}`} />
        <div className="font-mono text-xs text-[#A8A8A1] mt-3 w-12 shrink-0">
          {String(mIdx + 1).padStart(2, "0")}.{String(lIdx + 1).padStart(2, "0")}
        </div>
        <div className="flex-1 grid gap-2 min-w-0">
          <Input
            value={lesson.title}
            onChange={(e) => onUpdate(mIdx, lIdx, { title: e.target.value })}
            className="rounded-lg font-semibold"
            data-testid={`lesson-title-${mIdx}-${lIdx}`}
          />
          <Textarea
            rows={2}
            value={lesson.description || ""}
            onChange={(e) => onUpdate(mIdx, lIdx, { description: e.target.value })}
            className="rounded-lg text-sm"
            placeholder="Optional lesson description / notes"
            data-testid={`lesson-description-${mIdx}-${lIdx}`}
          />
          <div className="flex items-center gap-3 flex-wrap">
            {lesson.video_path ? (
              <span className="inline-flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5">
                <CheckCircle size={14} weight="fill" /> Video uploaded
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 text-xs text-[#5C5C58] bg-[#F4F4F0] border border-[#EAEAE6] px-3 py-1.5">
                <PlayCircle size={14} /> No video
              </span>
            )}
            {lesson.duration_seconds > 0 && (
              <span className="text-xs text-[#8A8A85] font-mono">
                {Math.round(lesson.duration_seconds / 60)}m {Math.round(lesson.duration_seconds) % 60}s
              </span>
            )}
            <label
              className="inline-flex items-center gap-2 text-xs border border-[#D8D8D2] hover:border-[#1C1C19] px-3 py-1.5 cursor-pointer"
              data-testid={`upload-video-label-${mIdx}-${lIdx}`}
            >
              <UploadSimple size={14} />{" "}
              {uploadingId === lesson.id
                ? `Uploading ${uploadProgress}%`
                : lesson.video_path
                  ? "Replace video"
                  : "Upload video"}
              <input
                type="file"
                accept="video/*"
                className="hidden"
                disabled={uploadingId === lesson.id}
                onChange={(e) => onUpload(mIdx, lIdx, e.target.files?.[0])}
                data-testid={`upload-video-input-${mIdx}-${lIdx}`}
              />
            </label>
          </div>
        </div>
        <button
          onClick={() => onRemove(mIdx, lIdx)}
          className="p-2 border border-[#EAEAE6] hover:border-[#993333] hover:text-[#993333]"
          data-testid={`remove-lesson-${mIdx}-${lIdx}`}
          title="Remove lesson"
        >
          <Trash size={14} />
        </button>
      </div>
    </div>
  );
}

function SortableModule({ id, mIdx, module: m, children, onUpdate, onRemove, collapsed, onToggleCollapse }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const lessonCount = (m.lessons || []).length;
  return (
    <div ref={setNodeRef} style={style} className="border border-[#EAEAE6] bg-white" data-testid={`module-${mIdx}`}>
      <div className="flex items-start gap-2 p-5 border-b border-[#EAEAE6] bg-[#F4F4F0]">
        <DragHandle listeners={listeners} attributes={attributes} label={`Reorder module ${mIdx + 1}`} />
        <button
          type="button"
          onClick={onToggleCollapse}
          className="p-2 mt-1 text-[#5C5C58] hover:text-[#1C1C19] hover:bg-white/60 rounded transition-colors shrink-0"
          aria-label={collapsed ? "Expand module" : "Collapse module"}
          title={collapsed ? "Expand module" : "Collapse module"}
          data-testid={`module-collapse-${mIdx}`}
        >
          {collapsed ? <CaretRight size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
        </button>
        <div className="font-mono text-xs text-[#A8A8A1] mt-3 w-8 shrink-0">
          {String(mIdx + 1).padStart(2, "0")}
        </div>
        <div className="flex-1 grid gap-2 min-w-0">
          <Input
            value={m.title}
            onChange={(e) => onUpdate(mIdx, { title: e.target.value })}
            className="rounded-lg font-heading text-lg font-semibold"
            data-testid={`module-title-${mIdx}`}
          />
          {!collapsed && (
            <Input
              value={m.description || ""}
              onChange={(e) => onUpdate(mIdx, { description: e.target.value })}
              className="rounded-lg text-sm"
              placeholder="Optional module description"
              data-testid={`module-description-${mIdx}`}
            />
          )}
          {collapsed && (
            <div className="text-xs text-[#8A8A85] mt-1">
              {lessonCount} {lessonCount === 1 ? "lesson" : "lessons"}
              {m.description ? ` · ${m.description}` : ""}
            </div>
          )}
        </div>
        <button
          onClick={() => onRemove(mIdx)}
          className="p-2 border border-[#EAEAE6] hover:border-[#993333] hover:text-[#993333]"
          data-testid={`remove-module-${mIdx}`}
          title="Remove module"
        >
          <Trash size={14} />
        </button>
      </div>
      {!collapsed && children}
    </div>
  );
}

export default function AdminCourseEdit() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [collapsedModules, setCollapsedModules] = useState({});

  const toggleModuleCollapse = (mId) =>
    setCollapsedModules((c) => ({ ...c, [mId]: !c[mId] }));
  const collapseAll = () => {
    const next = {};
    (course?.modules || []).forEach((m) => (next[m.id] = true));
    setCollapsedModules(next);
  };
  const expandAll = () => setCollapsedModules({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/courses/${courseId}`);
        setCourse(data);
      } catch (e) {
        setError(formatApiError(e, "Could not load course."));
      }
    })();
  }, [courseId]);

  const updateField = (field, value) => setCourse({ ...course, [field]: value });

  const updateModule = (mIdx, patch) => {
    const modules = [...course.modules];
    modules[mIdx] = { ...modules[mIdx], ...patch };
    setCourse({ ...course, modules });
  };

  const addModule = () => {
    setCourse({
      ...course,
      modules: [
        ...(course.modules || []),
        {
          id: newId(),
          title: "New module",
          description: "",
          lessons: [
            { id: newId(), title: "New lesson", description: "", duration_seconds: 0, video_path: "" },
          ],
        },
      ],
    });
  };

  const removeModule = (mIdx) => {
    const modules = [...course.modules];
    modules.splice(mIdx, 1);
    setCourse({ ...course, modules });
  };

  const addLesson = (mIdx) => {
    const modules = [...course.modules];
    modules[mIdx].lessons = [
      ...(modules[mIdx].lessons || []),
      { id: newId(), title: "New lesson", description: "", duration_seconds: 0, video_path: "" },
    ];
    setCourse({ ...course, modules });
  };

  const updateLesson = (mIdx, lIdx, patch) => {
    const modules = [...course.modules];
    modules[mIdx].lessons[lIdx] = { ...modules[mIdx].lessons[lIdx], ...patch };
    setCourse({ ...course, modules });
  };

  const removeLesson = (mIdx, lIdx) => {
    const modules = [...course.modules];
    modules[mIdx].lessons.splice(lIdx, 1);
    setCourse({ ...course, modules });
  };

  // dnd-kit sensors (pointer for mouse/touch, keyboard for a11y)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleModuleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = course.modules.findIndex((m) => m.id === active.id);
    const newIdx = course.modules.findIndex((m) => m.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setCourse({ ...course, modules: arrayMove(course.modules, oldIdx, newIdx) });
  };

  const handleLessonDragEnd = (mIdx) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const lessons = course.modules[mIdx].lessons || [];
    const oldIdx = lessons.findIndex((l) => l.id === active.id);
    const newIdx = lessons.findIndex((l) => l.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const modules = [...course.modules];
    modules[mIdx] = { ...modules[mIdx], lessons: arrayMove(lessons, oldIdx, newIdx) };
    setCourse({ ...course, modules });
  };

  const onUploadVideo = async (mIdx, lIdx, file) => {
    if (!file) return;
    const lessonId = course.modules[mIdx].lessons[lIdx].id;
    setUploadingId(lessonId);
    setUploadProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      // Try to read video duration on client
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;
      v.onloadedmetadata = () => {
        const duration = Math.round(v.duration || 0);
        URL.revokeObjectURL(url);
        updateLesson(mIdx, lIdx, { video_path: data.video_path, duration_seconds: duration });
      };
      v.onerror = () => {
        URL.revokeObjectURL(url);
        updateLesson(mIdx, lIdx, { video_path: data.video_path });
      };
      toast.success("Video uploaded");
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
    } finally {
      setUploadingId(null);
      setUploadProgress(0);
    }
  };

  const onUploadThumbnail = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateField("thumbnail_url", fileUrl(data.image_path));
      toast.success("Thumbnail uploaded");
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
    }
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(`/courses/${courseId}`, {
        title: course.title,
        description: course.description,
        category: course.category,
        level: course.level,
        thumbnail_url: course.thumbnail_url,
        published: course.published,
        modules: course.modules,
      });
      setCourse(data);
      toast.success("Course saved");
    } catch (e) {
      const msg = formatApiError(e, "Save failed");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!course) {
    return (
      <AppLayout title="Loading..." eyebrow="Edit course">
        <div className="text-sm text-[#8A8A85]">{error || "Loading course..."}</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={`Edit: ${course.title}`}
      eyebrow="Course editor"
      actions={
        <>
          <Link
            to="/admin/courses"
            className="px-4 py-2 border border-[#D8D8D2] hover:border-[#1C1C19] text-sm inline-flex items-center gap-2"
            data-testid="back-to-courses"
          >
            <CaretLeft size={16} /> Back
          </Link>
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg"
            data-testid="save-course-button"
          >
            <FloppyDisk size={16} className="mr-2" /> {saving ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      {error && <div className="text-sm text-[#993333] mb-4">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Course meta */}
        <div className="lg:col-span-1 space-y-5">
          <div className="border border-[#EAEAE6] bg-white p-5">
            <div className="eyebrow mb-3">Course details</div>
            <div className="space-y-4">
              <div>
                <Label className="eyebrow">Title</Label>
                <Input
                  value={course.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  className="mt-2 rounded-lg"
                  data-testid="edit-course-title"
                />
              </div>
              <div>
                <Label className="eyebrow">Description</Label>
                <Textarea
                  rows={4}
                  value={course.description || ""}
                  onChange={(e) => updateField("description", e.target.value)}
                  className="mt-2 rounded-lg"
                  data-testid="edit-course-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="eyebrow">Category</Label>
                  <Input
                    value={course.category || ""}
                    onChange={(e) => updateField("category", e.target.value)}
                    className="mt-2 rounded-lg"
                    data-testid="edit-course-category"
                  />
                </div>
                <div>
                  <Label className="eyebrow">Level</Label>
                  <Input
                    value={course.level || ""}
                    onChange={(e) => updateField("level", e.target.value)}
                    className="mt-2 rounded-lg"
                    data-testid="edit-course-level"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between border border-[#EAEAE6] px-4 py-3">
                <div>
                  <div className="font-semibold text-sm">Published</div>
                  <div className="text-xs text-[#8A8A85]">Visible to all employees</div>
                </div>
                <Switch
                  checked={!!course.published}
                  onCheckedChange={(v) => updateField("published", v)}
                  data-testid="edit-course-published-toggle"
                />
              </div>
            </div>
          </div>

          <div className="border border-[#EAEAE6] bg-white p-5">
            <div className="eyebrow mb-3">Thumbnail</div>
            <div className="aspect-video bg-[#EAEAE6] border border-[#EAEAE6] mb-3 overflow-hidden">
              {course.thumbnail_url ? (
                <img src={course.thumbnail_url} alt="thumbnail" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-[#A8A8A1] uppercase tracking-[0.15em]">
                  No thumbnail
                </div>
              )}
            </div>
            <label
              className="block w-full text-center border border-[#D8D8D2] hover:border-[#1C1C19] px-4 py-3 cursor-pointer text-sm font-medium"
              data-testid="upload-thumbnail-label"
            >
              <UploadSimple size={16} className="inline mr-2" /> Upload image
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onUploadThumbnail(e.target.files?.[0])}
                data-testid="upload-thumbnail-input"
              />
            </label>
            <Input
              placeholder="...or paste image URL"
              value={course.thumbnail_url || ""}
              onChange={(e) => updateField("thumbnail_url", e.target.value)}
              className="mt-3 rounded-lg"
              data-testid="thumbnail-url-input"
            />
          </div>
        </div>

        {/* Modules + Lessons */}
        <div className="lg:col-span-2 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Curriculum</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">
                {(course.modules || []).length} modules
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {(course.modules || []).length > 1 && (
                <div className="flex items-center text-xs">
                  <button
                    onClick={expandAll}
                    className="px-3 py-1.5 border border-[#D8D8D2] rounded-l-md hover:border-[#1C1C19] text-[#5C5C58] hover:text-[#1C1C19] transition-colors"
                    data-testid="modules-expand-all"
                  >
                    Expand all
                  </button>
                  <button
                    onClick={collapseAll}
                    className="px-3 py-1.5 border border-l-0 border-[#D8D8D2] rounded-r-md hover:border-[#1C1C19] text-[#5C5C58] hover:text-[#1C1C19] transition-colors"
                    data-testid="modules-collapse-all"
                  >
                    Collapse all
                  </button>
                </div>
              )}
              <Button
                onClick={addModule}
                className="bg-[#1C1C19] hover:bg-[#2A2A26] text-white rounded-lg"
                data-testid="add-module-button"
              >
                <Plus size={16} className="mr-2" /> Add module
              </Button>
            </div>
          </div>

          {(course.modules || []).length === 0 && (
            <div className="border border-[#E25822]/30 bg-[#E25822]/[0.03] p-10 text-center">
              <UploadSimple size={36} className="mx-auto mb-3 text-[#E25822]" weight="duotone" />
              <h3 className="font-heading text-xl font-bold mb-1">Ready to upload your first video?</h3>
              <p className="text-[#5C5C58] text-sm mb-5">
                Click <span className="font-semibold">Add module</span> above, then add a lesson — each lesson
                has an <span className="font-semibold">Upload video</span> button to attach an MP4 file (up to
                2 GB).
              </p>
              <Button
                onClick={addModule}
                className="bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg"
                data-testid="empty-add-module-button"
              >
                <Plus size={16} className="mr-2" /> Add your first module
              </Button>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleModuleDragEnd}
          >
            <SortableContext
              items={(course.modules || []).map((m) => m.id)}
              strategy={verticalListSortingStrategy}
            >
              {(course.modules || []).map((m, mIdx) => (
                <SortableModule
                  key={m.id}
                  id={m.id}
                  mIdx={mIdx}
                  module={m}
                  onUpdate={updateModule}
                  onRemove={removeModule}
                  collapsed={!!collapsedModules[m.id]}
                  onToggleCollapse={() => toggleModuleCollapse(m.id)}
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleLessonDragEnd(mIdx)}
                  >
                    <SortableContext
                      items={(m.lessons || []).map((l) => l.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="divide-y divide-[#F4F4F0]">
                        {(m.lessons || []).map((l, lIdx) => (
                          <SortableLesson
                            key={l.id}
                            id={l.id}
                            mIdx={mIdx}
                            lIdx={lIdx}
                            lesson={l}
                            uploadingId={uploadingId}
                            uploadProgress={uploadProgress}
                            onUpdate={updateLesson}
                            onRemove={removeLesson}
                            onUpload={onUploadVideo}
                          />
                        ))}
                        <div className="p-4">
                          <Button
                            variant="outline"
                            onClick={() => addLesson(mIdx)}
                            className="rounded-lg border-[#D8D8D2] hover:border-[#1C1C19]"
                            data-testid={`add-lesson-${mIdx}`}
                          >
                            <Plus size={14} className="mr-2" /> Add lesson
                          </Button>
                        </div>
                      </div>
                    </SortableContext>
                  </DndContext>
                </SortableModule>
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </AppLayout>
  );
}
